import { AlertTriangle } from 'lucide-react';

const currency = (value: number) => `৳${Number(value || 0).toLocaleString()}`;

export interface ReferralZeroPayoutDetails {
  /** Name of the referral partner selected on the receipt. */
  referralName: string;
  /** Effective referral share % for this receipt. */
  referralPercent: number;
  /** Projected referral payout (always zero when this warning is shown). */
  projectedPayout: number;
  /** Why the projection is zero — shown as a short explanation. */
  reason: string;
}

/**
 * Warning shown when a referral partner is selected but the receipt would
 * accrue no referral payout (0% effective share).
 * The caller blocks submission until the user either fixes the referral setup
 * or explicitly chooses to create the receipt without a payout.
 */
export function ReferralZeroPayoutModal({
  open,
  details,
  onClose,
  onProceed,
}: {
  open: boolean;
  details: ReferralZeroPayoutDetails;
  onClose: () => void;
  onProceed: () => void;
}) {
  if (!open) return null;

  const { referralName, referralPercent, projectedPayout, reason } = details;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-surface rounded-xl border border-border p-6 w-full max-w-md space-y-4 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-warning/10 flex items-center justify-center shrink-0">
            <AlertTriangle size={22} className="text-warning" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-bold text-text">No referral payout will be created</h3>
            <p className="text-sm text-text-muted mt-1">
              A referral commission is a percentage of each test's discount. With the current
              referral setup this receipt will not appear in Billing → Payouts.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-background p-3 space-y-2 text-sm">
          <div className="flex items-center justify-between gap-3">
            <span className="text-text-muted">Referral</span>
            <span className="font-medium text-text truncate">{referralName || 'Selected partner'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-text-muted">Referral %</span>
            <span className="font-medium text-text">{referralPercent}%</span>
          </div>
          <div className="h-px bg-border" />
          <div className="flex items-center justify-between">
            <span className="text-text-muted">Projected payout</span>
            <span className="font-bold text-danger">{currency(projectedPayout)}</span>
          </div>
        </div>

        <p className="text-xs text-text-muted">{reason}</p>

        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            onClick={onProceed}
            className="px-4 py-2 text-sm font-medium text-danger hover:underline transition-colors"
          >
            Create Without Payout
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
          >
            Go Back &amp; Fix
          </button>
        </div>
      </div>
    </div>
  );
}
